var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { addAccentBar, addCardChrome } from './chrome';
import { getActiveDiagram } from './theme';
import { drawSchematicGlyph, getSchematicSymbolMeta, resolveSchematicSymbolKind, SCHEMATIC_SYMBOL_SIZE, } from './schematicIcons';
var SYMBOL_SIZE = SCHEMATIC_SYMBOL_SIZE;
var ACCENT_H = 4;
/** Top accent stripe by schematic category (catalog / card chrome). */
function categoryAccent(category) {
    var d = getActiveDiagram();
    switch (category) {
        case 'power':
            return d.schematicBattery;
        case 'passive':
            return d.schematicResistor;
        case 'diode':
            return d.schematicLedStroke;
        case 'transistor':
        case 'thyristor':
            return '#a78bfa';
        case 'logic':
        case 'digital':
            return d.schematicSwitch;
        case 'analog':
            return '#22d3ee';
        case 'sensor':
            return '#34d399';
        case 'actuator':
            return '#fb7185';
        case 'switch':
            return d.schematicSwitch;
        case 'connector':
            return '#94a3b8';
        case 'comms':
            return '#38bdf8';
        case 'protection':
            return '#f97316';
        case 'test':
            return '#eab308';
        case 'mechanical':
            return '#64748b';
        default:
            return d.edgeMuted;
    }
}
/** Create an electrical schematic symbol (full electronic catalog). */
export function createSymbol(app, type, x, y, label) {
    var kind = resolveSchematicSymbolKind(type);
    var meta = getSchematicSymbolMeta(kind);
    var accent = categoryAccent(meta.category);
    if (kind === 'wire') {
        var g = app.group({ x: x, y: y });
        drawSchematicGlyph(app, g, kind);
        g.metadata = { symbolType: kind };
        return g;
    }
    // Outer wrapper holds label + accent; inner plate is hard-clipped to the rounded box.
    var wrap = app.group({ x: x, y: y });
    var radius = 4;
    var pad = app.group({ listening: false });
    // Rounded clip so strokes cannot leak through square corners past the fill.
    pad.mask = app.roundedRect({
        width: SYMBOL_SIZE,
        height: SYMBOL_SIZE,
        cornerRadius: radius,
        listening: false,
    });
    addCardChrome(app, pad, {
        width: SYMBOL_SIZE,
        height: SYMBOL_SIZE,
        cornerRadius: radius,
        fill: getActiveDiagram().schematicFill,
        stroke: getActiveDiagram().labelPillStroke,
        strokeWidth: 1,
        shadow: null,
        sheen: false,
        // Accent drawn on wrap (below) so it is never masked/clipped away.
        accentColor: undefined,
    });
    var topPad = ACCENT_H + 4;
    var sidePad = 6;
    var bottomPad = 6;
    var availW = SYMBOL_SIZE - sidePad * 2;
    var availH = SYMBOL_SIZE - topPad - bottomPad;
    var scale = Math.min(availW / SYMBOL_SIZE, availH / SYMBOL_SIZE) * 0.9;
    var glyphHost = app.group({
        x: (SYMBOL_SIZE - SYMBOL_SIZE * scale) / 2,
        y: topPad + (availH - SYMBOL_SIZE * scale) / 2,
        scaleX: scale,
        scaleY: scale,
        listening: false,
    });
    drawSchematicGlyph(app, glyphHost, kind);
    pad.add(glyphHost);
    wrap.add(pad);
    // Category strip on the wrapper — always visible for every symbol (fuse, GND, etc.).
    addAccentBar(app, wrap, SYMBOL_SIZE, accent, ACCENT_H);
    wrap.metadata = { symbolType: kind };
    if (label) {
        wrap.add(app.text({
            text: label,
            x: centerLabelX(label, SYMBOL_SIZE),
            y: SYMBOL_SIZE + 6,
            fontSize: getActiveDiagram().fontSize.sm,
            fontWeight: '600',
            fontFamily: getActiveDiagram().fontFamily,
            fill: getActiveDiagram().schematicLabel,
            listening: false,
        }));
    }
    return wrap;
}
function centerLabelX(label, boxWidth) {
    var approx = label.length * getActiveDiagram().fontSize.sm * 0.55;
    // Allow negative X so long labels stay centered under the glyph (not left-clipped).
    return (boxWidth - approx) / 2;
}
function junctionDot(app, x, y) {
    var g = app.group({ listening: false });
    g.add(app.circle({
        x: x,
        y: y,
        radius: 3,
        fill: getActiveDiagram().schematicWire,
        stroke: getActiveDiagram().surface,
        strokeWidth: 1,
        listening: false,
    }));
    return g;
}
function wireBetween(app, x1, y1, x2, y2) {
    var g = app.group({ listening: false });
    g.add(app.line({
        x: x1,
        y: y1,
        x2: x2 - x1,
        y2: y2 - y1,
        stroke: getActiveDiagram().schematicWireGlow,
        strokeWidth: 5,
        lineCap: 'round',
        opacity: 0.5,
        listening: false,
    }));
    g.add(app.line({
        x: x1,
        y: y1,
        x2: x2 - x1,
        y2: y2 - y1,
        stroke: getActiveDiagram().schematicWire,
        strokeWidth: 2.25,
        lineCap: 'round',
        listening: false,
    }));
    return g;
}
/** Build a schematic from component list */
export function buildSchematic(app, components) {
    var e_1, _a;
    var group = app.group({ name: 'schematic' });
    var wireLayer = app.group({ zIndex: -10, listening: false });
    wireLayer.metadata.diagramEdgeLayer = true;
    layoutSchematicWires(app, wireLayer, components);
    group.add(wireLayer);
    try {
        for (var components_1 = __values(components), components_1_1 = components_1.next(); !components_1_1.done; components_1_1 = components_1.next()) {
            var comp = components_1_1.value;
            var sym = createSymbol(app, comp.type, comp.x, comp.y, comp.label);
            sym.metadata.diagramId = comp.id;
            sym.metadata.diagramCardWidth = SYMBOL_SIZE;
            sym.metadata.diagramCardHeight = SYMBOL_SIZE;
            if (comp.rotation)
                sym.rotation = comp.rotation;
            group.add(sym);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (components_1_1 && !components_1_1.done && (_a = components_1.return)) _a.call(components_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return group;
}
/** Rebuild schematic wires from current symbol positions (live drag). */
export function rewireSchematic(app, root) {
    var e_2, _a;
    var wireLayer = root.children.find(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.diagramEdgeLayer; });
    if (!wireLayer) {
        wireLayer = app.group({ zIndex: -10, listening: false });
        wireLayer.metadata.diagramEdgeLayer = true;
        root.add(wireLayer);
    }
    else {
        try {
            for (var _b = __values(__spreadArray([], __read(wireLayer.children), false)), _c = _b.next(); !_c.done; _c = _b.next()) {
                var child = _c.value;
                wireLayer.remove(child);
                child.destroy();
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_2) throw e_2.error; }
        }
    }
    var comps = root.children
        .filter(function (c) { var _a, _b; return ((_a = c.metadata) === null || _a === void 0 ? void 0 : _a.symbolType) && ((_b = c.metadata) === null || _b === void 0 ? void 0 : _b.diagramId); })
        .map(function (c) { return ({
        id: c.metadata.diagramId,
        type: String(c.metadata.symbolType),
        x: c.x,
        y: c.y,
        rotation: c.rotation || 0,
    }); });
    layoutSchematicWires(app, wireLayer, comps);
    root.markDirty();
}
function schematicPort(c, side) {
    var lx = side === 'right' ? SYMBOL_SIZE : 0;
    var ly = SYMBOL_SIZE / 2;
    var rad = (((c.rotation || 0) * Math.PI) / 180);
    var cos = Math.cos(rad);
    var sin = Math.sin(rad);
    return {
        x: c.x + lx * cos - ly * sin,
        y: c.y + lx * sin + ly * cos,
    };
}
function layoutSchematicWires(app, wireLayer, components) {
    var sorted = __spreadArray([], __read(components), false).sort(function (a, b) { return a.x - b.x; });
    for (var i = 0; i < sorted.length - 1; i++) {
        var a = sorted[i];
        var b = sorted[i + 1];
        if (resolveSchematicSymbolKind(a.type) === 'wire' || resolveSchematicSymbolKind(b.type) === 'wire') {
            continue;
        }
        var p1 = schematicPort(a, 'right');
        var p2 = schematicPort(b, 'left');
        var x1 = p1.x;
        var y1 = p1.y;
        var x2 = p2.x;
        var y2 = p2.y;
        if (x2 <= x1 - 2 && Math.abs(y2 - y1) < 1)
            continue;
        if (Math.abs(y1 - y2) < 1.5) {
            if (x2 > x1) {
                wireLayer.add(wireBetween(app, x1, y1, x2, y2));
                wireLayer.add(junctionDot(app, x1, y1));
                wireLayer.add(junctionDot(app, x2, y2));
            }
        }
        else {
            var midX = (x1 + x2) / 2;
            wireLayer.add(wireBetween(app, x1, y1, midX, y1));
            wireLayer.add(wireBetween(app, midX, y1, midX, y2));
            wireLayer.add(wireBetween(app, midX, y2, x2, y2));
            wireLayer.add(junctionDot(app, x1, y1));
            wireLayer.add(junctionDot(app, midX, y1));
            wireLayer.add(junctionDot(app, midX, y2));
            wireLayer.add(junctionDot(app, x2, y2));
        }
    }
}
/** Legacy named factories kept for Symbols namespace compatibility. */
function resistor(app, x, y) {
    return createSymbol(app, 'resistor', x, y);
}
function capacitor(app, x, y) {
    return createSymbol(app, 'capacitor', x, y);
}
function ground(app, x, y) {
    return createSymbol(app, 'ground', x, y);
}
function battery(app, x, y) {
    return createSymbol(app, 'battery', x, y);
}
function switchSymbol(app, x, y) {
    return createSymbol(app, 'switch', x, y);
}
function led(app, x, y) {
    return createSymbol(app, 'led', x, y);
}
export var Symbols = {
    resistor: resistor,
    capacitor: capacitor,
    ground: ground,
    battery: battery,
    switch: switchSymbol,
    led: led,
    create: createSymbol,
    build: buildSchematic,
    meta: getSchematicSymbolMeta,
};
